import * as React from 'react';
import {
  SortingState, PagingState, FilteringState,
  IntegratedSorting, IntegratedPaging, IntegratedFiltering
} from '@devexpress/dx-react-grid';

import {
  Grid,
  Table, TableHeaderRow, TableFilterRow,
  PagingPanel, DragDropProvider, TableColumnReordering, TableColumnResizing
} from '@devexpress/dx-react-grid-material-ui';

import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import FormLabel from '@material-ui/core/FormLabel';
import Chip from '@material-ui/core/Chip';
import { withStyles } from '@material-ui/core/styles';
import { API } from 'aws-amplify';

const styles = theme => ({
  dialog: { width: 'calc(100% - 16px)' },
  headerBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '40px 20px 20px 20px'
  },
  countPill: { marginLeft: 12 }
});

// --- Helpers to read either plain JSON or DynamoDB AttributeValue JSON ---
const fromAttr = (v) => (v && typeof v === 'object' && ('S' in v || 'N' in v))
  ? (v.S ?? v.N ?? v.BOOL ?? v.NULL ?? v)
  : v;

const normalizeItem = (item) => {
  // Supports both shapes:
  // 1) { id:"...", title:"...", description:"...", createdAt:"...", status:"pending", videos:"a,b" }
  // 2) DynamoDB Attr JSON like you posted.
  const id = fromAttr(item.id) || item.id;
  const title = fromAttr(item.title) || item.title;
  const description = fromAttr(item.description) || item.description;
  const status = fromAttr(item.status) || item.status;
  const createdAt = fromAttr(item.createdAt) || item.createdAt;
  const videos = fromAttr(item.videos) || item.videos;

  return {
    id,
    title,
    description,
    status,
    createdAt,               // ISO string or anything parseable by Date
    videos,                  // comma-separated string
    videosCount: (videos && typeof videos === 'string' && videos.trim() !== '')
      ? videos.split(',').length
      : 0
  };
};

const getRowId = row => row.id;

class Notifications extends React.PureComponent {
  state = {
    columns: [
      { name: 'title',       title: 'Title' },
      { name: 'description', title: 'Description' },
      { name: 'videosCount', title: 'Videos' },
      { name: 'videos',      title: 'Video IDs (CSV)' },
      { name: 'createdAt',   title: 'Created At' }
    ],
    // sensible default widths; tweak as you like
    defaultColumnWidths: [
      { columnName: 'title',       width: 240 },
      { columnName: 'description', width: 340 },
      { columnName: 'videosCount', width: 100 },
      { columnName: 'videos',      width: 360 },
      { columnName: 'createdAt',   width: 200 }
    ],
    columnOrder: ['title','description','status','videosCount','videos','createdAt'],
    rows: [],
    sorting: [],
    currentPage: 0,
    pageSize: 50,
    pageSizes: [0, 50, 100],
    loading: false,
  };

  componentDidMount() {
    if (this.props.isAuthenticated === false) return;
    this.loadNotifications();
  }

  loadNotifications = async () => {
    this.setState({ loading: true });
    try {
      // 👇 adjust path if your route differs (e.g., "/push-notifications")
      const data = await API.get('pushnotification', '/push-notification');
      // Data might be: an array of items OR { Items: [...] } from DynamoDB passthrough
      const list = Array.isArray(data) ? data : (Array.isArray(data?.Items) ? data.Items : []);
      const rows = list.map(normalizeItem).sort((a,b) => {
        // newest first by createdAt if available
        const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return db - da;
      });
      this.setState({ rows });
    } catch (e) {
      console.error('Failed to load notifications', e);
      alert('❌ Failed to load notifications');
    } finally {
      this.setState({ loading: false });
    }
  };

  changeSorting = sorting => this.setState({ sorting });
  changeCurrentPage = currentPage => this.setState({ currentPage });
  changePageSize = pageSize => this.setState({ pageSize });
  changeColumnOrder = order => this.setState({ columnOrder: order });

  // Custom cell renderers
  Cell = (props) => {
    const { column, value, row } = props;

    if (column.name === 'status') {
      const color = (row.status || '').toLowerCase() === 'sent' ? 'primary' :
                    (row.status || '').toLowerCase() === 'failed' ? 'secondary' : 'default';
      return (
        <Table.Cell {...props}>
          <Chip label={row.status || '—'} color={color} />
        </Table.Cell>
      );
    }

    if (column.name === 'videosCount') {
      return (
        <Table.Cell {...props}>
          <Chip label={row.videosCount} />
        </Table.Cell>
      );
    }

    if (column.name === 'createdAt') {
      // Human-friendly local time (works in India/US/anywhere)
      const dt = row.createdAt ? new Date(row.createdAt) : null;
      const pretty = dt ? dt.toLocaleString() : '—';
      return <Table.Cell {...props}>{pretty}</Table.Cell>;
    }

    return <Table.Cell {...props} />;
  };

  render() {
    const { classes } = this.props;
    const {
      rows, columns, sorting, currentPage, pageSize, pageSizes, columnOrder, defaultColumnWidths, loading
    } = this.state;

    return (
      <Paper>
        <div className={classes.headerBar}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FormLabel style={{ marginLeft: 20, marginTop: 40, fontSize: 32, marginBottom: 40 }}>
              Push Notifications
            </FormLabel>
            <Chip
              className={classes.countPill}
              label={`${rows.length} total`}
            />
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={this.loadNotifications}
            disabled={loading}
            style={{ backgroundColor: '#00BCD4', color: '#fff', textTransform: 'capitalize' }}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>

        <Grid rows={rows} columns={columns} getRowId={getRowId}>
          <SortingState sorting={sorting} onSortingChange={this.changeSorting} />
          <PagingState
            currentPage={currentPage}
            onCurrentPageChange={this.changeCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={this.changePageSize}
          />
          <FilteringState />

          <IntegratedSorting />
          <IntegratedPaging />
          <IntegratedFiltering />

          <DragDropProvider />
          <Table cellComponent={this.Cell} />
          <TableColumnReordering order={columnOrder} onOrderChange={this.changeColumnOrder} />
          <TableColumnResizing defaultColumnWidths={defaultColumnWidths} />
          <TableHeaderRow showSortingControls />
          <TableFilterRow />
          <PagingPanel pageSizes={pageSizes} />
        </Grid>
      </Paper>
    );
  }
}

export default withStyles(styles, { name: 'Notifications' })(Notifications);